import router from '../router';
import { createStore } from 'vuex';
import createPersistedState from 'vuex-persistedstate';
import axios from 'axios';
import modal from './modules/modal';
import bookingManager from '@/utils/booking';

const api = axios.create({
  baseURL: '/api', // Replace with your FastAPI server URL
});

const  store = createStore({
    modules: {
        modal,
    },
  state: {
    isAuthenticated: false,
    user: null,
    showServerErrorModal: false,
    token: localStorage.getItem('token') || '',
    error: '',
    errorMessages: {},
    notifications: [],
  },
  mutations: {
    initState(state) {
      if (state.token && isTokenExpired(state.token)) {
        state.isAuthenticated = false;
        state.token = '';
        state.user = null;
        localStorage.removeItem('token');
      } else {
        state.isAuthenticated = true;
      }
      state.showServerErrorModal = false
      state.error = '';
      state.errorMessages = {};
    },
    loginSuccess(state, { token, user }) {
      state.isAuthenticated = true;
      state.user = user;
      state.token = token;
      state.showServerErrorModal = false
      state.error = '';
      state.errorMessages = {};
      localStorage.setItem('token', token);
    },
    loginFailure(state, { error, errorMessages }) {
      state.isAuthenticated = false;
      state.user = null;
      state.token = '';
      state.showServerErrorModal = false
      state.error = error || 'An error occurred.';
      state.errorMessages = errorMessages || {};
      state.notifications = [];
      localStorage.removeItem('token');
    },
    setUser(state, user) {
      state.user = user;
    },
    setNotifications(state, notifications) {
      state.notifications = notifications
    },
    showServerErrorModal(state) {
        state.showServerErrorModal = true
    },
    hideServerErrorModal(state) {
        state.showServerErrorModal = false
    },
    logout(state) {
      state.isAuthenticated = false;
      state.user = null;
      state.token = '';
      state.error = '';
      state.errorMessages = {};
      state.notifications = [];
      localStorage.removeItem('token');
    },
  },
  actions: {
    async initState({ commit }) {
      commit('initState');
    },
    async refreshToken({ commit, state }) {
      console.log('Attempting token refresh...',state.token);
      try {
        // const token = localStorage.getItem('token');
        const response = await axios.post('/api/auth/jwt/refresh', {}, {
          headers: { Authorization: `Bearer ${state.token}` },
        });
        const newToken = response.data.access_token;
        console.log('New Token:', newToken);
        // Update state and token in localStorage
        commit('loginSuccess', { token: newToken, user: state.user });
      } catch (err) {
        console.error('Token refresh failed:', err);
        commit('logout');
        router.push({ name: 'login' });
      }
    },
    async login({ commit }, { email, password, redirectTo = '/' }) {
      commit('initState');
      try {
        // Create a URLSearchParams object to send data as x-www-form-urlencoded
        const formData = new URLSearchParams();
        formData.append('username', email);
        formData.append('password', password);
    
        // Send the request as form data
        const response = await api.post('/auth/jwt/login', formData, {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        });
    
        // Extract the token and user data
        const token = response.data.access_token;
        const userResponse = await api.get('/users/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const user = userResponse.data;
    
        // Commit the success actions
        commit('loginSuccess', { token, user });
        let prebookingToken = bookingManager.getToken();
        if (!user.is_verified) {
          // If not verified, redirect to OTP verification page
          router.push('/my/profile')
          if (prebookingToken && !bookingManager.isTokenExpired()) {
            this.dispatch('modal/triggerModal', {
                icon: 'warning',
                icon_color: 'text-warning',
                title: 'Attention!',
                detail: 'Veuillez vérifier votre compte par e-mail pour finaliser votre réservation.'
            })
          } else if (!prebookingToken || bookingManager.isTokenExpired()) {
            bookingManager.removeToken()
            router.push('/')
          }
          // router.push({ name: 'otp-verification' });
        } else {
          if (prebookingToken && !bookingManager.isTokenExpired()) {
            await bookingManager.prebook(prebookingToken.facility_id, prebookingToken);
          } else if (!prebookingToken || bookingManager.isTokenExpired()) {
            bookingManager.removeToken()
            router.push('/')
          }
        }
      } catch (err) {
        const errorData = err.response && err.response.data ? err.response.data : {};
        console.log("err", err);
        
        commit('loginFailure', {
          error: errorData?.detail || 'Login failed.',
          errorMessages: errorData?.messages || {},
        });
      }
    },
    async socialLogin({ commit }, { provider = 'google', code, state, scope }) {
      commit('initState');
      console.log(provider)
      try {
        let params = {code, state, scope}
        if (!scope) {
            provider = 'facebook'
            params = {code, state}
        }
        // // Step 2: Handle the callback and extract the authorization code
        const tokenResponse = await api.get(`/auth/login/${provider}/callback`, {params: params});
        const accessToken = tokenResponse.data.access_token;
    
        // Step 4: Retrieve user details with the access token
        const userResponse = await api.get('/users/me', {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const user = userResponse.data;
    
        // Step 5: Commit login success
        commit('loginSuccess', { token: accessToken, user });
        let prebookingToken = bookingManager.getToken();
        if (!user.is_verified) {
          // If not verified, redirect to OTP verification page
          router.push('/my/profile')
          if (prebookingToken && !bookingManager.isTokenExpired()) {
            this.dispatch('modal/triggerModal', {
                icon: 'warning',
                icon_color: 'text-warning',
                title: 'Attention!',
                detail: 'Veuillez vérifier votre compte par e-mail pour finaliser votre réservation.'
            })
          } else if (!prebookingToken || bookingManager.isTokenExpired()) {
            bookingManager.removeToken()
            router.push('/')
          }
          // router.push({ name: 'otp-verification' });
        } else {
          if (prebookingToken && !bookingManager.isTokenExpired()) {
            await bookingManager.prebook(prebookingToken.facility_id, prebookingToken);
          } else if (!prebookingToken || bookingManager.isTokenExpired()) {
            bookingManager.removeToken()
            router.push('/')
          }
        }
      } catch (err) {
        const errorData =
          err.response && err.response.data ? err.response.data : {};
        commit('loginFailure', {
          error: errorData?.detail || 'Social login failed.',
          errorMessages: errorData?.messages || {},
        });
      }
    },
    async fetchUser({ commit, state }) {
      if (!state.token) {
        return; // if no token, do not fetch user
      }
      try {
        const response = await api.get('/users/me', {
          headers: { Authorization: `Bearer ${state.token}` },
        });
        const user = await response.data;
        commit('setUser', user);
      } catch (error) {
        console.error('Error fetching user data:', error);
        // commit('clearUser'); // Optional: clear user if the request fails
      }
    },    
    async logout({ commit }) {
      const token = localStorage.getItem('token');
      try {
        await api.post('/auth/jwt/logout', {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
        commit('logout');
        router.push({ name: 'login' });
      } catch (err) {
        const errorData = err.response && err.response.data ? err.response.data : {};
        commit('loginFailure', {
          error: errorData?.detail || 'Logout failed.',
          errorMessages: errorData?.messages || {},
        });
      }
    },
    async toggleServerErrorModal({ commit }, { show }) {
        if (show) {
            commit('showServerErrorModal')
        } else {
            commit('hideServerErrorModal')
        }
    },
    async fetchNotifications({ commit, state }) {
      try {
        const response = await axios.get('/api/notifications/unread', {
          headers: { Authorization: `Bearer ${state.token}` },
        });
        const notifications = await response.data;
        commit('setNotifications', notifications);
      } catch (error) {
        console.error('Error fetching notifications data:', error);
        // commit('clearUser'); // Optional: clear user if the request fails
      }
    }
  },
  getters: {
    isLoggedIn: state => state.isAuthenticated,
    currentUser: state => Object.assign({}, state.user),
    error: state => state.error,
    showServerErrorModal: state => state.showServerErrorModal,
    errorMessages: state => state.errorMessages,
    notifications: state => state.notifications,
  },
  plugins: [createPersistedState({
    paths: ['isAuthenticated', 'user', 'token', 'notifications', 'error', 'errorMessages', 'modal'],
  })],
});

function redirect(user) {
  let redirectTo = 'my/bookings'
  if (user.role === "player") {
    router.push({name:"search"})
  } else {
    router.push({"name": "bookings"})
  }
}

function isTokenExpired(token) {
  try {
    const decoded = JSON.parse(atob(token.split(".")[1]));
    return decoded.exp * 1000 < Date.now();
  } catch {
    return true
  }
  
}

let isRefreshing = false; // Guard flag to avoid infinite loop
let failedQueue = []; // Queue to store failed requests during token refresh

function processQueue(error, token = null) {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
}

api.interceptors.request.use(
  async (config) => {
    let token = localStorage.getItem('token');
    // console.log("request",isTokenExpired(token));

    if (token && isTokenExpired(token)) {
      // If the token is expired, we should refresh it
      const requestConfig = config; // Save the request config

      // Add the current request to the queue
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });

        if (!isRefreshing) {
          isRefreshing = true;
          // console.log("here");
          

          store.dispatch('refreshToken')
            .then(() => {
              token = localStorage.getItem('token');
              requestConfig.headers.Authorization = `Bearer ${token}`;
              isRefreshing = false;
              // console.log("Updated token:", token);
              resolve(api(requestConfig)); // Retry the original request
              processQueue(null, token); // Process all queued requests with the new token
            })
            .catch((error) => {
              console.error('Refresh token error:', error);
              isRefreshing = false;
              processQueue(error); // Reject all failed requests
              reject(error); // Reject the current request
            });
        }
      });
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  async (response) => {
    const requestUrl = response.config.url
    if (store.getters.isLoggedIn && requestUrl != '/notifications/unread') {
      await store.dispatch('fetchNotifications');
    }
    return response
  },
  async (error) => {
    if (error.response && error.response.status === 401) {
      // If the token is invalid (401), handle the refresh flow
      // console.log("checking");

      
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          await store.dispatch('refreshToken');
          isRefreshing = false;

          // Retry the original request with a new token
          const token = localStorage.getItem('token');
          if(token){
            error.config.headers.Authorization = `Bearer ${token}`;
            return api.request(error.config); // Retry original request
          }
        } catch (err) {
          isRefreshing = false;
          store.commit('logout');
          router.push({ name: 'login' });
          throw err
        }
      }

      // If a refresh is already in progress, queue the failed request
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      });
    } else if (error.response?.status === 500) {
        store.dispatch('modal/triggerModal', {
            icon: 'error',
            icon_color: 'text-danger',
            title: 'Erreur',
            detail: 'Une erreur est survenue! Veuillez réessayer plus tard.',
        })
        return Promise.resolve(null);
    }

    return Promise.reject(error);
  },
);



export { store ,api };
